import React from "react";
import { cn } from "@shared/ui";
import PhoneInput from "react-phone-number-input";
import { isPossiblePhoneNumber } from "react-phone-number-input/input";
import type { Country } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { InlineError } from "./Field";

interface PhoneInputFieldProps {
  value: string;
  onChange: (value: string) => void;
  country: Country;
  onCountryChange: (country: Country) => void;
  showCountryFlags?: boolean;
  countries?: Country[];
  error?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  autoComplete?: string;
  "aria-label"?: string;
  tabIndex?: number;
}

// Validation messages shown under the phone field rather than in a screen-level error box.
export const PHONE_FIELD_ERRORS = {
  missing: "Enter your phone number",
  invalid: "Enter a valid phone number",
};

export const isPhoneFieldError = (error: string) =>
  Object.values(PHONE_FIELD_ERRORS).includes(error);

export const validatePhoneNumber = (
  phone: string | undefined,
  country: Country
): boolean => {
  if (!phone) return false;
  return isPossiblePhoneNumber(phone, country);
};

export default function PhoneInputField({
  value,
  onChange,
  country,
  onCountryChange,
  showCountryFlags = true,
  error,
  placeholder = "e.g. (555) 123-4567",
  className = "flex h-control-lg w-full items-center gap-2 rounded border border-control bg-surface px-3 transition-colors hover:border-ink-secondary focus-within:border-ink focus-within:ring-1 focus-within:ring-ink",
  disabled = false,
  autoComplete = "tel",
  "aria-label": ariaLabel = "Phone number",
  countries = ["US"],
  tabIndex = 0,
}: PhoneInputFieldProps) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor="phone-number" className="text-body-medium text-ink">
        Phone number
      </label>
      <div className="relative [&_.PhoneInputInput]:h-full [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:text-body-lg [&_.PhoneInputInput]:text-ink [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:placeholder:text-ink-secondary">
        <PhoneInput
          id="phone-number"
          defaultCountry="US"
          value={value}
          onChange={(value) => onChange(value || "")}
          onCountryChange={(value) => onCountryChange(value || "US")}
          placeholder={placeholder}
          className={cn(className, error && "border-danger-ink")}
          autoComplete={autoComplete}
          aria-label={ariaLabel}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "phone-number-error" : undefined}
          tabIndex={tabIndex}
          disabled={disabled}
          flagComponent={showCountryFlags ? undefined : () => null}
          countries={countries}
        />
      </div>

      {error && <InlineError id="phone-number-error">{error}</InlineError>}
    </div>
  );
}
